import http from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import { Values } from '../../../forms/Company/CompanyForm.types';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import { CompanyForm } from '../../../forms/Company/CompanyForm';
import { useLocation, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Company } from '../../../types/Company';
import { toast } from 'react-toastify';
import { Loader } from '../../../components/loader/Loader';
import { CompanyTeamAssign } from '../../../forms/CompanyTeamAssign/CompanyTeamAssing';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { TabsComponent } from '../../../components/tabs/Tabs';
import { CurrencyRatesPage } from '../CurrencyRates/CurrencyRatesPage';
import {
  convertEnumToArray,
  filterObjectFromEmptyString,
} from '../../../utils/functions/data.functions';
import { useTranslation } from '../../../context/translationContext';
import { NumericalSeriesPage } from '../NumericalSeries/NumericalSeriesPage';
import { CompanyBankAccountsPage } from '../CompanyBankAccount/CompanyBankAccountsPage';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { paths } from '../../RouterContainer';
import { DocumentOptionalFieldsPage } from '../DocumentOptionalFields/DocumentOptionalFieldsPage';
import { CompanyLogoPage } from '../CompanyLogo/CompanyLogoPage';
import useSessionStorage from '../../../hooks/useSessionStorage';
import { sessionStorageKeys } from '../../../utils/keys/sessionStorageKeys';
import { useLoading } from '../../../context/loadingContext';

export enum COMPANY_TABS {
  MAIN_SETTING = 'tab-setting',
  TEAMS = 'tab-teams',
  CURRENCY_RATES = 'tab-currency-rates',
  BANK_ACCOUNTS = 'tab-bank-accounts',
  NUMERICAL_SERIES = 'tab-numerical-series',
  OPTIONAL_FIELDS = 'tab-optional-fields',
  LOGO = 'tab-logo',
}

/**
 * Page component represents Company edit page with tabs.
 */
// TODO: Podle mě, bychom měli udělat samostatnou komponentu s taby o úrpveň výš. Něco jako UserCompaniesTabsContainer. Pomůže to přehlednosti.

export const UserCompaniesUpdatePage = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const { company: currentCompany, changeCompany } = useCurrentCompany();
  const { t } = useTranslation();
  const [companyId, setCompanyId] = useState<number>();
  const [activeTab, setActiveTab] = useSessionStorage<string>(
    sessionStorageKeys.COMPANY_ACTIVE_TAB,
    COMPANY_TABS.MAIN_SETTING,
  );
  const { startLoading, stopLoading } = useLoading();
  // Company to edit
  const [company, setCompany] = useState<Company | null>(null);
  const { setBreadcrumbs } = useBreadcrumbs();

  useEffect(() => {
    setBreadcrumbs([
      {
        path: `${paths.MY_COMPANIES}`,
        name: 'navigation.my-companies',
      },
      {
        path: `${paths.MY_COMPANIES}/edit/${id ? id : currentCompany?.id}`,
        name: 'navigation.company-setting',
      },
    ]);
  }, []);

  useEffect(() => {
    if (!state) return;
    const { tab } = state;
    if (tab) setActiveTab(tab);
  }, []);

  useEffect(() => {
    if (!id && !currentCompany) return;
    if (id) setCompanyId(Number(id));
    if (!id && currentCompany) setCompanyId(currentCompany.id);
  }, [id, currentCompany]);

  /**
   * Loads company to edit.
   */
  const fetchCompany = async () => {
    if (!companyId) return;
    const response = await http.apiGet({
      url: `${apiUrl.COMPANY_GET}?id=${companyId}`,
    });
    setCompany(response?.data);
  };

  useEffect(() => {
    if ((!companyId && !currentCompany) || companyId === company?.id) return;
    if (activeTab === COMPANY_TABS.MAIN_SETTING) fetchCompany();
  }, [companyId, currentCompany, activeTab, id]);

  /**
   * Handles edit action for update company form submit.
   * Uses company edit API.
   * @param values form data
   */
  const handleSubmit = async (values: Values) => {
    try {
      startLoading();
      const filteredData = filterObjectFromEmptyString(values);
      const response = await http.apiPut({ url: apiUrl.COMPANY_UPDATE, data: filteredData });
      if (response?.status === 200) {
        if (response?.data && response?.data.id === currentCompany?.id)
          changeCompany(response?.data);
        stopLoading();
        toast.success('Success!');
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  if (!currentCompany && !company && !id) return <Loader />;
  return (
    <>
      <h1 className="text-3xl font-bold">{t('page.company.headline-edit')}</h1>
      <div className="flex flex-row">
        <div className="w-full">
          <TabsComponent
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabs={convertEnumToArray(COMPANY_TABS)}
          >
            <div key={COMPANY_TABS.MAIN_SETTING}>
              {company && <CompanyForm onSubmit={handleSubmit} initialValues={company as Values} />}
            </div>
            <div key={COMPANY_TABS.TEAMS}>
              <CompanyTeamAssign companyId={Number(companyId)} />
            </div>
            <div key={COMPANY_TABS.CURRENCY_RATES}>
              <CurrencyRatesPage companyId={Number(companyId)} />
            </div>
            <div key={COMPANY_TABS.BANK_ACCOUNTS}>
              <CompanyBankAccountsPage companyId={Number(companyId)} />
            </div>
            <div key={COMPANY_TABS.NUMERICAL_SERIES}>
              <NumericalSeriesPage companyId={Number(companyId)} />
            </div>
            <div key={COMPANY_TABS.OPTIONAL_FIELDS}>
              <DocumentOptionalFieldsPage companyId={Number(companyId)} />
            </div>
            <div key={COMPANY_TABS.LOGO}>
              <CompanyLogoPage companyId={Number(companyId)} />
            </div>
          </TabsComponent>
        </div>
      </div>
    </>
  );
};
